export type SupportedValue = string | string[] | number | boolean | null;
export type FilteredSupportedValue = Exclude<SupportedValue, null>;
export type TableData = Record<string, SupportedValue>;
export interface FilterSearchOption {
  enabled?: boolean;
  searchKeys?: string[];
  searchValue?: string;
}

/**
 * Type guard to check if a value can be searched
 */
export const isSupportedSearchableValue = (
  value: unknown
): value is Exclude<SupportedValue, null> => {
  const type = typeof value;
  return (
    type === "string" ||
    type === "number" ||
    type === "boolean" ||
    Array.isArray(value) ||
    type === null
  );
};

/**
 * Extract unique values from a dataset for a specific column
 * Handles both string and string[] values by flattening arrays
 */
const extractUniqueValues = (
  dataSource: TableData[],
  columnKey: string
): SupportedValue[] => {
  if (!dataSource.length) return [];

  const allValues = new Set<string>();

  dataSource.forEach((item) => {
    const value = item[columnKey];
    if (isSupportedSearchableValue(value)) {
      if (Array.isArray(value)) {
        // For arrays, add each element separately
        value.forEach((arrayItem) => {
          if (typeof arrayItem === "string" && arrayItem.trim()) {
            allValues.add(arrayItem.trim());
          }
        });
      } else if (typeof value === "string" && value.trim()) {
        allValues.add(value.trim());
      } else if (typeof value === "number" || typeof value === "boolean") {
        allValues.add(String(value));
      }
    }
  });

  return Array.from(allValues);
};

/**
 * Filter out null and undefined values
 */
const filterValidValues = (
  values: SupportedValue[]
): FilteredSupportedValue[] => {
  return values.filter((value): value is FilteredSupportedValue => {
    return value !== null && value !== undefined;
  });
};

/**
 * Sort values with numeric awareness
 */
const sortValues = (values: FilteredSupportedValue[]) => {
  return values.sort((a, b) => {
    const aNum = Number(a);
    const bNum = Number(b);

    // If both are valid numbers, sort numerically
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    // Otherwise sort alphabetically
    return String(a).localeCompare(String(b));
  });
};

/**
 * Map values to filter options compatible with Antd's filter format (String)
 */
const mapToFilterOptions = (
  values: FilteredSupportedValue[]
): { text: string; value: string }[] => {
  return values.map((value) => ({ text: String(value), value: String(value) }));
};

/**
 * Helper function to check if a record value matches a filter value
 * Handles both string and string[] record values
 */
export const recordMatchesFilter = (
  recordValue: SupportedValue,
  filterValue: string
): boolean => {
  if (recordValue === null || recordValue === undefined) return false;

  const filterLower = filterValue.toLowerCase();

  if (Array.isArray(recordValue)) {
    // For arrays, check if any element contains the filter value
    return recordValue.some((item) =>
      String(item).toLowerCase().includes(filterLower)
    );
  }

  // For non-arrays, convert to string and check
  return String(recordValue).toLowerCase().includes(filterLower);
};

/**
 * Helper function to get sortable string from record value
 * Handles both string and string[] values for consistent sorting
 */
export const getSortableString = (value: SupportedValue): string => {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return String(value);
};

/**
 * Create filter options for a table column
 * Extracts unique values, filters valid ones, sorts them, and maps to filter format
 */
export const createFiltersForColumn = (
  dataSource: TableData[],
  columnKey: string
): { text: string; value: string }[] => {
  const uniqueValues = extractUniqueValues(dataSource, columnKey);
  const validValues = filterValidValues(uniqueValues);
  const sortedValues = sortValues(validValues);
  return mapToFilterOptions(sortedValues);
};
